import { apiErrorMessage } from '@/utils/apiError'
import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { toast } from 'vue-sonner'
import { api } from '@/services/api'
import { usePi } from '@/services/pi'
import type { User, AuthResponse } from '@/types/api'
import type { PiUser } from '@/services/pi'
import { identifyUser, track } from '@/services/analytics'

const PI_IDENTITY_KEY = 'ts_pi_identity'
const REFRESH_TOKEN_KEY = 'auth_refresh_token'
const REVOKE_TIMEOUT_MS = 5000
const NEW_ACCOUNT_WINDOW_MS = 10 * 60 * 1000

/** The auth response has no "created" flag, so an account made minutes ago counts as a sign-up. */
function isNewAccount(user: User) {
  return !!user.createdAt && Date.now() - Date.parse(user.createdAt) < NEW_ACCOUNT_WINDOW_MS
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const token = ref<string | null>(localStorage.getItem('auth_token'))
  const user = ref<User | null>(null)
  const initialized = ref(false)
  const isLoading = ref(false)
  let initialization: Promise<void> | undefined
  
  // Pi
  const pi = usePi()

  watch(() => user.value?.id ?? null, identifyUser, { immediate: true })

  // Computed
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isAdmin = computed(() => user.value?.role === 'admin')
  const isModerator = computed(() => user.value?.role === 'moderator' || user.value?.role === 'admin')
  const welcomeSuppressed = computed(() => !!sessionStorage.getItem(PI_IDENTITY_KEY))

  // Actions
  function initAuth(): Promise<void> {
    if (initialized.value) return Promise.resolve()
    if (initialization) return initialization

    initialization = (async () => {
      const savedToken = localStorage.getItem('auth_token')
      if (savedToken) {
        token.value = savedToken
        try {
          const userData = await api.getMe()
          user.value = userData
        } catch {
          // Token invalid
          await logout()
        }
      }

      initialized.value = true
    })()

    return initialization
  }

  async function loginWithPi(accessToken: string, refreshToken?: string, piUser?: PiUser) {
    const response = await piBrowserAuth(accessToken, refreshToken, piUser)
    track('Signed In', { method: 'pi-browser', new_user: isNewAccount(response.user) })
    return response
  }

  /** Signs a Pi Sign-in account in through Pi Browser once, which links its wallet. Not a new sign-in. */
  function linkPiWallet(accessToken: string, refreshToken?: string, piUser?: PiUser) {
    return piBrowserAuth(accessToken, refreshToken, piUser)
  }

  async function piBrowserAuth(accessToken: string, refreshToken?: string, piUser?: PiUser) {
    isLoading.value = true
    try {
      const response: AuthResponse = await api.piAuth(
        accessToken,
        refreshToken,
        'pi-browser',
        piUser?.walletAddress,
      )
      setAuth(response.token, response.user, response.refreshToken)
      if (piUser?.uid && piUser?.username) {
        sessionStorage.setItem(PI_IDENTITY_KEY, JSON.stringify(piUser))
      }
      return response
    } finally {
      isLoading.value = false
    }
  }

  async function loginWithPiSignIn(accessToken: string, walletAddress?: string) {
    isLoading.value = true
    try {
      const response: AuthResponse = await api.piAuth(
        accessToken,
        undefined,
        'pi-signin',
        walletAddress,
      )
      setAuth(response.token, response.user, response.refreshToken)
      track('Signed In', { method: 'pi-signin', new_user: isNewAccount(response.user) })
      return response
    } finally {
      isLoading.value = false
    }
  }

  function setAuth(newToken: string, newUser: User, newRefreshToken?: string) {
    token.value = newToken
    user.value = newUser
    localStorage.setItem('auth_token', newToken)
    if (newRefreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken)
    }
  }

  /**
   * Ends the session on the server and waits for the answer. Resolves false when that could
   * not be confirmed (offline, server error, timeout). The refresh token is kept in that case,
   * so the caller can retry: once it is cleared locally nothing can revoke it any more.
   */
  async function revokeSession(): Promise<boolean> {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
    if (!refreshToken) return true
    let timer: ReturnType<typeof setTimeout> | undefined
    try {
      await Promise.race([
        api.revokeSession(refreshToken),
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => reject(new Error('Session revocation timed out')), REVOKE_TIMEOUT_MS)
        }),
      ])
      return true
    } catch {
      return false
    } finally {
      clearTimeout(timer)
    }
  }

  /** Clears the session on this device. Pass `revoked` when `revokeSession()` already succeeded. */
  async function logout(options: { revoked?: boolean } = {}) {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
    token.value = null
    user.value = null
    localStorage.removeItem('auth_token')
    localStorage.removeItem(REFRESH_TOKEN_KEY)
    sessionStorage.removeItem(PI_IDENTITY_KEY)
    // Best-effort server-side revocation; unknown/expired tokens are ignored.
    if (refreshToken && !options.revoked) {
      api.revokeSession(refreshToken).catch(() => {})
    }
  }

  // Tabs share localStorage. When another tab signs out, drop the in-memory session here too
  // so this tab stops showing and acting on data that belongs to a closed session.
  window.addEventListener('storage', (event) => {
    if (event.key === 'auth_token' && event.newValue === null) {
      token.value = null
      user.value = null
    }
  })

  async function refreshUser() {
    if (!token.value) return
    try {
      const userData = await api.getMe()
      user.value = userData
    } catch {
      await logout()
    }
  }

  async function updateProfile(payload: Partial<User>) {
    if (!token.value) return
    try {
      const updated = await api.updateProfile(payload)
      user.value = updated
      return updated
    } catch (err: any) {
      addToast('error', apiErrorMessage(err, 'Failed to update profile'))
      throw err
    }
  }

  function addToast(type: 'success' | 'error' | 'info', message: string) {
    if (type === 'success') {
      toast.success(message)
    } else if (type === 'error') {
      toast.error(message)
    } else {
      toast.info(message)
    }
  }

  return {
    // State
    token,
    user,
    initialized,
    isLoading,
    // Computed
    isAuthenticated,
    isAdmin,
    isModerator,
    welcomeSuppressed,
    // Actions
    initAuth,
    setAuth,
    loginWithPi,
    linkPiWallet,
    loginWithPiSignIn,
    logout,
    revokeSession,
    refreshUser,
    updateProfile,
    addToast,
    // Pi
    pi,
  }
})
